import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Button } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useTranslation } from "react-i18next";
import { RichTextEditor } from "../../../../components/RichTextEditor";
import {
  InfoBanner,
  InfoBannerText,
  SummarizedViewContainer,
  SummarizedHeaderRow,
  SummarizedTitle,
  EditIconButton,
  AppointmentBlock,
  AppointmentHeadingRow,
  AppointmentHeadingText,
  AppointmentOptionLabel,
  TimeframeBadge,
  BulletItem,
  BulletDot,
  DescriptionLine,
  IntroParagraph,
  WarningBox,
  WarningText,
  SectionLabel,
  EditActions,
  EditorErrorText,
} from "./SummarizedView.styled";
import { colors } from "../../../../theme/colors";
import { EditNoteIcon } from "../../../../components/Icons";
import {
  parseSummarizedContent,
  summarizedContentToEditorHtml,
  summarizedContentToPlainText,
} from "../../../../utils/parseSummarizedContent";

export interface SummarizedViewHandle {
  startEdit: () => void;
}

interface SummarizedViewProps {
  content: string;
  canEdit?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
  onSave: (content: string) => Promise<void>;
}

const EDITOR_LINE_HEIGHT = 24;
const EDITOR_MIN_ROWS = 12;

const SummarizedView = forwardRef<SummarizedViewHandle, SummarizedViewProps>(
  ({ content, canEdit = false, onEditingChange, onSave }, ref) => {
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(content);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
      if (!isEditing) {
        setEditValue(content);
      }
    }, [content, isEditing]);

    useEffect(() => {
      return () => {
        onEditingChange?.(false);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStartEdit = useCallback(() => {
      setEditValue(summarizedContentToEditorHtml(content));
      setSaveError(null);
      setIsEditing(true);
      onEditingChange?.(true);
    }, [content, onEditingChange]);

    useImperativeHandle(ref, () => ({ startEdit: handleStartEdit }), [
      handleStartEdit,
    ]);

    const handleCancel = useCallback(() => {
      setEditValue(content);
      setSaveError(null);
      setIsEditing(false);
      onEditingChange?.(false);
    }, [content, onEditingChange]);

    const handleSave = useCallback(async () => {
      setSaveError(null);
      setIsSaving(true);
      try {
        await onSave(editValue);
        setIsEditing(false);
        onEditingChange?.(false);
      } catch {
        setSaveError(t("treatmentPlan.summarizedViewSaveFailed"));
      } finally {
        setIsSaving(false);
      }
    }, [editValue, onSave, onEditingChange, t]);

    const editorMinHeight =
      Math.max(
        EDITOR_MIN_ROWS,
        summarizedContentToPlainText(editValue).split("\n").length + 3,
      ) * EDITOR_LINE_HEIGHT;

    if (isEditing) {
      return (
        <>
          <WarningBox>
            <WarningAmberRoundedIcon
              sx={{ color: colors.amber.main, flexShrink: 0, mt: "1px" }}
            />
            <WarningText>
              {t("treatmentPlan.summarizedViewWarning")}
            </WarningText>
          </WarningBox>

          <SectionLabel>
            {t("treatmentPlan.summarizedViewEditLabel")}
          </SectionLabel>

          <RichTextEditor
            value={editValue}
            onChange={setEditValue}
            minHeight={editorMinHeight}
          />

          {saveError && <EditorErrorText>{saveError}</EditorErrorText>}

          <EditActions>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t("treatmentPlan.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              sx={{
                backgroundColor: colors.primary.main,
                "&:hover": { backgroundColor: colors.primary.dark },
              }}
            >
              {isSaving ? t("common.saving") : t("treatmentPlan.saveChanges")}
            </Button>
          </EditActions>
        </>
      );
    }

    const parsed = parseSummarizedContent(content);

    const nodes: React.ReactNode[] = [];
    let introBuffer: string[] = [];
    let currentApptBlock: {
      key: string;
      label: string;
      optionLabel?: string;
      timeframe?: string;
      children: React.ReactNode[];
    } | null = null;

    const flushApptBlock = () => {
      if (currentApptBlock) {
        nodes.push(
          <AppointmentBlock key={currentApptBlock.key}>
            <AppointmentHeadingRow>
              <AppointmentHeadingText>
                {currentApptBlock.label}
              </AppointmentHeadingText>
              {currentApptBlock.optionLabel && (
                <AppointmentOptionLabel>
                  {currentApptBlock.optionLabel}
                </AppointmentOptionLabel>
              )}
              {currentApptBlock.timeframe && (
                <TimeframeBadge>{currentApptBlock.timeframe}</TimeframeBadge>
              )}
            </AppointmentHeadingRow>
            {currentApptBlock.children}
          </AppointmentBlock>,
        );
        currentApptBlock = null;
      }
    };

    const flushIntro = () => {
      if (introBuffer.length > 0) {
        nodes.push(
          <IntroParagraph key={`intro-${nodes.length}`}>
            {introBuffer.join(" ")}
          </IntroParagraph>,
        );
        introBuffer = [];
      }
    };

    for (let i = 0; i < parsed.length; i++) {
      const line = parsed[i];

      if (line.type === "intro") {
        introBuffer.push(line.text);
        continue;
      }

      flushIntro();

      if (line.type === "empty") continue;

      if (line.type === "appointment") {
        flushApptBlock();
        currentApptBlock = {
          key: `appt-${i}`,
          label: line.appointmentLabel || line.text,
          optionLabel: line.optionLabel,
          timeframe: line.timeframe,
          children: [],
        };
        continue;
      }

      if (line.type === "bullet" && currentApptBlock) {
        currentApptBlock.children.push(
          <BulletItem key={`bullet-${i}`}>
            <BulletDot>•</BulletDot>
            {line.text}
          </BulletItem>,
        );
        continue;
      }

      if (line.type === "description" && currentApptBlock) {
        currentApptBlock.children.push(
          <DescriptionLine key={`desc-${i}`}>{line.text}</DescriptionLine>,
        );
        continue;
      }
    }

    flushIntro();
    flushApptBlock();

    return (
      <>
        <SummarizedHeaderRow>
          <SummarizedTitle>
            {t("treatmentPlan.treatmentPlanSummary")}
          </SummarizedTitle>
          {canEdit && (
            <EditIconButton
              onClick={handleStartEdit}
              aria-label={t("treatmentPlan.editPlan")}
            >
              <EditNoteIcon width="20px" height="20px" />
            </EditIconButton>
          )}
        </SummarizedHeaderRow>
        <InfoBanner>
          <InfoOutlinedIcon
            sx={{
              color: colors.primary.main,
              flexShrink: 0,
              mt: "1px",
              fontSize: "18px",
            }}
          />
          <InfoBannerText>
            {t("treatmentPlan.summarizedViewInfoMessage")}
          </InfoBannerText>
        </InfoBanner>
        <SummarizedViewContainer>{nodes}</SummarizedViewContainer>
      </>
    );
  },
);

SummarizedView.displayName = "SummarizedView";

export default SummarizedView;
